<template>
  <main class="grow learn-page">
    <section class="learn-hero">
      <div class="learn-wrap learn-hero-grid" :class="{ 'has-visual': heroReplica }">
        <div class="learn-hero-copy">
        <nav class="learn-breadcrumb" aria-label="Breadcrumb">
          <NuxtLink :to="localePath('/learn')">Learning center</NuxtLink>
          <span aria-hidden="true">/</span>
          <span>{{ page.badge }}</span>
        </nav>
        <h1>{{ page.h1 }}</h1>
        <p class="learn-lede">{{ page.lede }}</p>
        <p class="learn-reviewed">Reviewed <time :datetime="page.reviewedOn">{{ useFormatDate(page.reviewedOn) }}</time></p>
        </div>
        <StoryReplicaStage
          v-if="heroReplica"
          class="learn-hero-visual"
          :replica="heroReplica.replica"
          :states="heroReplica.states"
          :state="heroReplica.states[0]"
          :label="heroReplica.label"
          tall
        />
      </div>
    </section>

    <div class="learn-wrap learn-layout">
      <!-- Section list: sticky on wide screens, hidden on phones (the article is short enough to scroll). -->
      <nav class="learn-toc" aria-label="Sections">
        <a v-for="(section, index) in page.sections" :key="section.title" :href="`#section-${index}`">{{ section.title }}</a>
        <a href="#faq">Common questions</a>
      </nav>

      <article class="learn-article">
        <details class="learn-toc-mobile">
          <summary>On this page</summary>
          <nav aria-label="Sections (short list)">
            <a v-for="(section, index) in page.sections" :key="section.title" :href="`#section-${index}`">{{ section.title }}</a>
            <a href="#faq">Common questions</a>
          </nav>
        </details>
        <div class="learn-answer-panel">
          <p class="learn-kicker">In short</p>
          <p class="learn-answer-copy">{{ page.answer }}</p>
        </div>

        <section
          v-for="(section, index) in page.sections"
          :id="`section-${index}`"
          :key="section.title"
          class="learn-section"
        >
          <h2>{{ section.title }}</h2>
          <p class="learn-section-copy">{{ section.copy }}</p>
          <ul v-if="section.bullets?.length" class="learn-bullets">
            <li v-for="bullet in section.bullets" :key="bullet">{{ bullet }}</li>
          </ul>
          <p v-if="section.after" class="learn-section-copy">{{ section.after }}</p>
          <p v-if="section.links?.length" class="learn-section-links">
            Read next:
            <template v-for="(link, linkIndex) in section.links" :key="link.path">
              <NuxtLink :to="localePath(link.path)">{{ link.label }}</NuxtLink><span v-if="linkIndex < section.links.length - 1">, </span>
            </template>
          </p>
          <div v-if="section.trial" class="learn-section-trial">
            <a
              :href="addReferralToUrl(`https://app.workspace369.com/signup?plan=${section.trial.plan}`)"
              class="learn-section-trial-btn"
              data-conversion-event="start_free_click"
              :data-conversion-label="section.trial.label"
              :data-conversion-location="`learn-${page.slug}-section-${index}`"
              :data-conversion-plan="section.trial.plan"
            >{{ section.trial.label }}</a>
            <TrialDisclosure :plan="section.trial.plan" />
          </div>
        </section>

        <section id="faq" class="learn-section learn-faq">
          <h2>Common questions</h2>
          <div class="learn-faq-list">
            <details v-for="item in page.faq" :key="item.q" class="learn-faq-item">
              <summary>
                <span>{{ item.q }}</span>
                <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m4.5 6 3.5 3.5L11.5 6" /></svg>
              </summary>
              <p>{{ item.a }}</p>
            </details>
          </div>
        </section>
      </article>
    </div>

    <FreelancerOfferSection v-if="offerPlan" :location="`learn-${page.slug}`" :featured="offerPlan" />

    <section class="learn-related">
      <div class="learn-wrap">
        <div class="learn-related-heading">
          <h2>Related guides and tools</h2>
        </div>
        <nav class="learn-related-grid" aria-label="Related Workspace369 pages">
          <NuxtLink
            v-for="link in page.relatedLinks"
            :key="link.path"
            :to="localePath(link.path)"
            class="learn-related-card"
          >
            <span>{{ link.label }}</span>
            <span aria-hidden="true">→</span>
          </NuxtLink>
        </nav>
      </div>
    </section>

    <RelatedPages
      :path="`/learn/${page.slug}`"
      :title="page.h1"
      :exclude="page.relatedLinks.map((link) => link.path)"
    />

    <Cta03 />
    <PhoneStickyCta label="Learn guide" :location="$route.path" watch=".learn-hero" />
  </main>
</template>

<script setup>
import { localizedUrl } from '~/data/locales';
import articleDates from '~/data/article-dates.json';
import { freelancerLearnGuides } from '~/data/freelancer-offer-pages';

const props = defineProps({
  page: { type: Object, required: true },
});

const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const { addReferralToUrl } = useReferral();
// Guides whose topic has a matching app screen show that real replica beside the headline.
const heroReplicas = {
  "how-to-write-an-invoice": {
    replica: "invoice-editor",
    states: ["payment"],
    label: "The Workspace369 invoice editor: client, line items with quantity and rate, the total, and the payment and online page settings.",
  },
};
const heroReplica = computed(() => heroReplicas[props.page.slug]);
const offerPlan = computed(() => freelancerLearnGuides[props.page.slug]);
const siteUrl = 'https://workspace369.com';
const canonicalUrl = computed(() =>
  localizedUrl(siteUrl, `/learn/${props.page.slug}`, canonicalLocaleCode.value),
);

useHead(() => ({
  title: props.page.metaTitle,
  meta: [
    { name: 'description', content: props.page.description },
    { name: 'twitter:title', content: props.page.metaTitle },
    { name: 'twitter:description', content: props.page.description },
    { name: 'twitter:card', content: 'summary_large_image' },
    { property: 'og:title', content: props.page.metaTitle },
    { property: 'og:description', content: props.page.description },
  ],
  link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl.value }],
  script: [
    {
      key: 'learn-breadcrumb-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: localizedUrl(siteUrl, '/', canonicalLocaleCode.value) },
          { '@type': 'ListItem', position: 2, name: 'Learning center', item: localizedUrl(siteUrl, '/learn', canonicalLocaleCode.value) },
          { '@type': 'ListItem', position: 3, name: props.page.badge, item: canonicalUrl.value },
        ],
      }),
    },
    {
      key: 'learn-article-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        '@id': `${canonicalUrl.value}#article`,
        headline: props.page.h1,
        description: props.page.description,
        url: canonicalUrl.value,
        mainEntityOfPage: canonicalUrl.value,
        image: `${siteUrl}/images/og/page-learn-${props.page.slug}-2026.jpg`,
        inLanguage: canonicalLocaleCode.value,
        // First listed in seo-routes (data/article-dates.json); never later than the last review.
        datePublished: [articleDates[`/learn/${props.page.slug}`]?.published, props.page.reviewedOn].filter(Boolean).sort()[0],
        dateModified: props.page.reviewedOn,
        author: { '@type': 'Organization', '@id': `${siteUrl}/#organization`, name: 'Workspace369', url: `${siteUrl}/` },
        publisher: { '@type': 'Organization', '@id': `${siteUrl}/#organization`, name: 'Workspace369', url: `${siteUrl}/` },
        ...(props.page.term ? {
          about: {
            '@type': 'DefinedTerm',
            '@id': `${canonicalUrl.value}#term`,
            name: props.page.term,
            description: props.page.answer,
            url: canonicalUrl.value,
            inDefinedTermSet: { '@type': 'DefinedTermSet', name: 'Learning center', url: localizedUrl(siteUrl, '/learn', canonicalLocaleCode.value) },
          },
        } : {}),
      }),
    },
    {
      key: 'learn-faq-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${canonicalUrl.value}#faq`,
        mainEntity: props.page.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
/* App tokens (InvoicesAppWindow): #171717 ink, #e6e6e6 hairlines, 10px surfaces, 4px markers. No kicker pills,
   no tinted accent panel, no alternating section bands: one article column with a sticky section list. */
.learn-page {
  --ink: #0a0a0a; --ink2: #4f4f4f; --ink3: #717171; --edge: #e6e6e6; --canvas: #f7f7f7; --hover: rgb(10 10 10 / 0.04);
  background: #fff;
  color: var(--ink);
}
.dark .learn-page {
  --ink: #fafafa; --ink2: #c2c2c2; --ink3: #9a9a9a; --edge: #2c2c2c; --canvas: #1a1a1a; --hover: rgb(250 250 250 / 0.05);
  background: #121212;
}
.learn-wrap { max-width: 72rem; margin-inline: auto; padding-inline: 1.25rem; }
@media (min-width: 640px) { .learn-wrap { padding-inline: 1.75rem; } }

.learn-hero { padding: 8.5rem 0 2rem; }
.learn-hero-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 2rem; }
.learn-hero-visual.sst { width: 100%; border-radius: 10px; }
@media (max-width: 1023px) { .learn-hero-visual.sst { height: 440px; } }
@media (min-width: 1024px) {
  .learn-hero-grid.has-visual { grid-template-columns: minmax(0, 1fr) minmax(0, 30rem); gap: 4rem; align-items: center; }
}
.learn-breadcrumb { display: flex; flex-wrap: wrap; gap: 0.5rem; color: var(--ink3); font-size: 0.85rem; }
.learn-breadcrumb a { color: var(--ink2); text-decoration: underline; text-underline-offset: 2px; }
.learn-hero h1 { max-width: 46rem; margin-top: 1.25rem; font-size: clamp(1.9rem, 4.6vw, 3.25rem); font-weight: 780; letter-spacing: -0.01em; line-height: 1.07; text-wrap: balance; }
.learn-lede { max-width: 40rem; margin-top: 1.1rem; color: var(--ink2); font-size: clamp(0.95rem, 2.4vw, 1.05rem); line-height: 1.7; }
.learn-reviewed { margin-top: 0.9rem; color: var(--ink3); font-size: 0.82rem; }

.learn-layout { display: grid; grid-template-columns: minmax(0, 1fr); gap: 2rem; padding-bottom: 3.5rem; }
.learn-toc { display: none; }
@media (min-width: 1024px) {
  .learn-layout { grid-template-columns: 14rem minmax(0, 44rem); gap: 4rem; }
  .learn-toc { position: sticky; top: 8rem; align-self: start; display: grid; gap: 0.15rem; border-left: 1px solid var(--edge); }
  .learn-toc a { margin-left: -1px; border-left: 1px solid transparent; padding: 0.35rem 0.9rem; color: var(--ink3); font-size: 0.85rem; line-height: 1.4; text-decoration: none; }
  .dark .learn-toc a { color: var(--ink2); }
  .learn-toc a:hover, .learn-toc a:focus-visible { border-left-color: var(--ink); color: var(--ink); }
}

.learn-toc-mobile { margin-bottom: 1rem; border: 1px solid var(--edge); border-radius: 8px; }
.learn-toc-mobile summary { display: flex; align-items: center; justify-content: space-between; list-style: none; cursor: pointer; padding: 0.7rem 1rem; font-size: 0.9rem; font-weight: 600; }
.learn-toc-mobile summary::-webkit-details-marker { display: none; }
.learn-toc-mobile summary::after { content: ''; width: 7px; height: 7px; border-right: 1.5px solid currentColor; border-bottom: 1.5px solid currentColor; transform: rotate(45deg); transition: transform 150ms ease; }
.learn-toc-mobile[open] summary::after { transform: rotate(225deg); }
.learn-toc-mobile nav { display: grid; border-top: 1px solid var(--edge); }
.learn-toc-mobile a { padding: 0.55rem 1rem; color: var(--ink2); font-size: 0.88rem; text-decoration: none; }
@media (min-width: 1024px) { .learn-toc-mobile { display: none; } }
.learn-answer-panel { border: 1px solid var(--edge); border-radius: 8px; background: var(--canvas); padding: 1rem 1.15rem; }
.learn-kicker { color: var(--ink3); font-size: 0.8rem; font-weight: 600; }
.learn-answer-copy { margin-top: 0.35rem; color: var(--ink); font-size: 1rem; line-height: 1.7; }
@media (max-width: 639px) { .learn-answer-panel { padding: 0.8rem 0.95rem; } .learn-answer-copy { font-size: 0.9rem; line-height: 1.5; } }

.learn-section { scroll-margin-top: 8rem; margin-top: 2.5rem; border-top: 1px solid var(--edge); padding-top: 2rem; }
.learn-section h2 { font-size: clamp(1.35rem, 2.6vw, 1.7rem); font-weight: 720; letter-spacing: -0.005em; line-height: 1.2; }
.learn-section-copy { margin-top: 0.8rem; color: var(--ink2); font-size: 1rem; line-height: 1.75; }
.learn-bullets { margin: 1rem 0 0; padding: 0; list-style: none; display: grid; gap: 0.6rem; }
.learn-bullets li { position: relative; padding-left: 1.4rem; color: var(--ink); font-size: 0.98rem; line-height: 1.6; }
.learn-bullets li::before { content: ""; position: absolute; left: 0.1rem; top: 0.55rem; width: 8px; height: 8px; border-radius: 2px; background: var(--ink); }
.learn-section-links { margin-top: 1rem; color: var(--ink2); font-size: 0.95rem; line-height: 1.6; }
.learn-section-links a { color: var(--ink); font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }
.learn-section-trial { margin-top: 1.25rem; display: grid; justify-items: start; gap: 0.5rem; }
.learn-section-trial-btn { display: inline-flex; align-items: center; min-height: 44px; border-radius: 8px; background: var(--ink); padding: 0.65rem 1.1rem; color: var(--canvas); font-size: 0.95rem; font-weight: 600; text-decoration: none; }
.learn-section-trial-btn:hover, .learn-section-trial-btn:focus-visible { opacity: 0.88; }

.learn-faq-list { margin-top: 1rem; overflow: hidden; border: 1px solid var(--edge); border-radius: 10px; }
.learn-faq-item + .learn-faq-item { border-top: 1px solid var(--edge); }
.learn-faq-item summary { display: flex; align-items: center; justify-content: space-between; gap: 1rem; cursor: pointer; padding: 0.9rem 1.1rem; color: var(--ink); font-size: 0.95rem; font-weight: 600; list-style: none; }
.learn-faq-item summary::-webkit-details-marker { display: none; }
.learn-faq-item summary:hover { background: var(--hover); }
.learn-faq-item summary svg { flex: none; width: 1rem; height: 1rem; color: var(--ink3); transition: transform 120ms ease; }
.learn-faq-item[open] summary svg { transform: rotate(180deg); }
.learn-faq-item p { padding: 0 1.1rem 1rem; color: var(--ink2); font-size: 0.95rem; line-height: 1.7; }

.learn-related { border-top: 1px solid var(--edge); padding: 2.75rem 0; }
.learn-related-heading h2 { font-size: clamp(1.35rem, 2.6vw, 1.7rem); font-weight: 720; line-height: 1.2; }
.learn-related-grid { margin-top: 1.25rem; display: grid; max-width: 44rem; overflow: hidden; border: 1px solid var(--edge); border-radius: 10px; }
.learn-related-card { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 0.85rem 1.1rem; color: var(--ink); font-size: 0.95rem; font-weight: 600; text-decoration: none; }
.learn-related-card + .learn-related-card { border-top: 1px solid var(--edge); }
.learn-related-card:hover { background: var(--hover); }
.learn-related-card > span:last-child { color: var(--ink3); }
@media (prefers-reduced-motion: reduce) { .learn-faq-item summary svg { transition: none; } }
</style>
